<script lang="ts">
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import type { Coordinates } from '$lib/features/_other/coordinates.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { ANALYTICS_EVENT_NAMES, SITE_NAME } from '$lib/statics';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconClose from '~icons/mdi/close';
	import IconCompass from '~icons/mdi/explore';

	interface ShareStop {
		name: string;
		coordinates: Coordinates;
		imageUrl?: string;
	}

	interface ComputedRoute {
		waypoints: Waypoint[];
		routeSvgPath: string;
		auxiliaryTrails: string[];
	}

	interface Waypoint {
		name: string;
		imageUrl: string | undefined;
		lineX: number;
		lineY: number;
		bubbleX: number;
		bubbleY: number;
		isAbove: boolean;
		index: number;
	}

	let {
		isOpen = $bindable(false),
		title,
		cityName,
		countryName,
		stops,
		cityHeroImage,
		stats = []
	}: {
		isOpen: boolean;
		title: string;
		cityName: string;
		countryName: string;
		stops: ShareStop[];
		cityHeroImage: CoverImage;
		stats: { label: string; value: string }[];
	} = $props();

	const analyticsApi = new ClientVelatlasLoggerApi();
	const SVG_WIDTH = 1200;
	const SVG_HEIGHT = 675;

	let dialogRef = $state<HTMLDialogElement | null>(null);

	const locationSubtitle = $derived.by(() => {
		const locationLabel = countryName ? `${cityName}, ${countryName}` : cityName;
		return `${stops.length} stops across ${locationLabel}`;
	});
	const computedRoute = $derived.by<ComputedRoute>(() => {
		if (!stops || stops.length === 0) {
			return { waypoints: [], routeSvgPath: '', auxiliaryTrails: [] };
		}

		const count = stops.length;
		const startX = 200;
		const endX = SVG_WIDTH - 200;
		const usableWidth = endX - startX;
		const baselineY = SVG_HEIGHT * 0.48;

		// Calculate cumulative distances
		const distances: number[] = [0];
		let totalDist = 0;
		for (let i = 1; i < count; i++) {
			const prev = stops[i - 1].coordinates;
			const curr = stops[i].coordinates;
			const dLat = curr.latitude - prev.latitude;
			const dLng = (curr.longitude - prev.longitude) * Math.cos((prev.latitude * Math.PI) / 180);
			const d = Math.sqrt(dLat * dLat + dLng * dLng);
			totalDist += Math.max(d, 0.0001);
			distances.push(totalDist);
		}

		// Horizontal spacing with anti-collision
		const MIN_HORIZONTAL_SPACING = 150;
		let rawX = stops.map((_, i) => {
			const progress = count > 1 ? distances[i] / totalDist : 0.5;
			return startX + progress * usableWidth;
		});

		for (let i = 1; i < rawX.length; i++) {
			if (rawX[i] - rawX[i - 1] < MIN_HORIZONTAL_SPACING) {
				rawX[i] = rawX[i - 1] + MIN_HORIZONTAL_SPACING;
			}
		}

		const maxCalculatedX = rawX[rawX.length - 1];
		if (maxCalculatedX > endX) {
			const scale = (endX - startX) / (maxCalculatedX - startX);
			rawX = rawX.map((x) => startX + (x - startX) * scale);
		}

		// Construct waypoints with strictly vertical, alternating tethers
		const waypoints: Waypoint[] = stops.map((stop, i) => {
			const x = rawX[i];
			const lineY = baselineY + Math.sin(i * 1.5 + 0.3) * 28;
			const isAbove = i % 2 === 1;
			const stemLength = 88;

			let bubbleY = isAbove ? lineY - stemLength : lineY + stemLength;
			bubbleY = isAbove ? Math.max(85, bubbleY) : Math.min(SVG_HEIGHT - 185, bubbleY);

			return {
				name: stop.name,
				imageUrl: stop.imageUrl,
				lineX: x,
				lineY,
				bubbleX: x,
				bubbleY,
				isAbove,
				index: i + 1
			};
		});

		// Continuous route ribbon through all stops
		const denseTrailPoints: { x: number; y: number }[] = [];
		for (let i = 0; i < waypoints.length - 1; i++) {
			const p1 = waypoints[i];
			const p2 = waypoints[i + 1];
			denseTrailPoints.push({ x: p1.lineX, y: p1.lineY });

			const dist = Math.hypot(p2.lineX - p1.lineX, p2.lineY - p1.lineY);
			const steps = Math.max(4, Math.floor(dist / 40));

			for (let s = 1; s < steps; s++) {
				const t = s / steps;
				const lx = p1.lineX + (p2.lineX - p1.lineX) * t;
				const ly = p1.lineY + (p2.lineY - p1.lineY) * t;

				const normalX = -(p2.lineY - p1.lineY) / dist;
				const normalY = (p2.lineX - p1.lineX) / dist;
				const wiggle = Math.sin(t * Math.PI * 2 + i * 1.4) * 12;

				denseTrailPoints.push({
					x: lx + normalX * wiggle,
					y: ly + normalY * wiggle
				});
			}
		}
		denseTrailPoints.push({
			x: waypoints[waypoints.length - 1].lineX,
			y: waypoints[waypoints.length - 1].lineY
		});

		const routeSvgPath = pointsToSpline(denseTrailPoints);

		// Auxiliary Dotted Trails
		const auxiliaryTrails: string[] = [];
		if (waypoints.length > 0) {
			const first = waypoints[0];
			auxiliaryTrails.push(
				pointsToSpline([
					{ x: Math.max(30, first.lineX - 140), y: Math.max(80, first.lineY - 70) },
					{ x: first.lineX - 70, y: first.lineY - 45 },
					{ x: first.lineX, y: first.lineY }
				])
			);

			const last = waypoints[waypoints.length - 1];
			auxiliaryTrails.push(
				pointsToSpline([
					{ x: last.lineX, y: last.lineY },
					{ x: last.lineX + 70, y: last.lineY - 20 },
					{ x: Math.min(SVG_WIDTH - 30, last.lineX + 140), y: last.lineY - 35 }
				])
			);
		}

		return {
			waypoints,
			routeSvgPath,
			auxiliaryTrails
		};
	});

	function pointsToSpline(pts: { x: number; y: number }[]): string {
		if (pts.length < 2) return '';
		let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
		for (let i = 0; i < pts.length - 1; i++) {
			const p0 = i > 0 ? pts[i - 1] : pts[i];
			const p1 = pts[i];
			const p2 = pts[i + 1];
			const p3 = i < pts.length - 2 ? pts[i + 2] : p2;

			const cp1x = p1.x + (p2.x - p0.x) / 6;
			const cp1y = p1.y + (p2.y - p0.y) / 6;
			const cp2x = p2.x - (p3.x - p1.x) / 6;
			const cp2y = p2.y - (p3.y - p1.y) / 6;

			d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
		}
		return d;
	}

	$effect(() => {
		if (isOpen) {
			analyticsApi.sendAnalytics({ event: ANALYTICS_EVENT_NAMES.routeShareOpened });

			if (dialogRef && !dialogRef.open) {
				dialogRef.showModal();
			}
		} else {
			if (dialogRef && dialogRef.open) {
				dialogRef.close();
			}
		}
	});
</script>

<dialog bind:this={dialogRef} class="modal z-100" onclose={() => (isOpen = false)}>
	<div class="relative modal-box max-w-5xl overflow-hidden rounded-box border border-white/10 bg-neutral-950 p-0">
		{@render CloseButton()}

		<div class="flex flex-col items-center p-3 sm:p-5">
			{@render Postcard()}
		</div>

		{@render ModalFooter()}
	</div>

	{@render ModalBackdrop()}
</dialog>

{#snippet CloseButton()}
	<form method="dialog">
		<button
			class="btn absolute top-3 right-3 z-40 btn-circle bg-black/60 btn-ghost text-white btn-sm hover:bg-black/90"
			aria-label="Close modal"
		>
			<IconClose class="text-lg" />
		</button>
	</form>
{/snippet}

{#snippet Postcard()}
	<div class="relative h-[52vh] w-full overflow-hidden rounded-box select-none sm:aspect-video sm:h-auto">
		{@render Background()}
		{@render RouteSvg()}
		{@render Waypoints()}
		{@render ItineraryDetails()}
	</div>
{/snippet}

{#snippet Background()}
	<img
		class="filter absolute inset-0 h-full w-full object-cover object-center brightness-[0.68] contrast-[1.1] saturate-[1.15]"
		src={UrlUtil.getFullImageUrl(cityHeroImage)}
		alt={cityHeroImage.altText}
	/>

	<div class="pointer-events-none absolute inset-0 bg-linear-to-t from-black/85 via-black/25 to-black/50"></div>
{/snippet}

{#snippet RouteSvg()}
	<svg
		class="pointer-events-none absolute inset-0 z-10 h-full w-full"
		viewBox="0 0 {SVG_WIDTH} {SVG_HEIGHT}"
		preserveAspectRatio="none"
	>
		<defs>
			<filter id="trailShadow" x="-20%" y="-20%" width="140%" height="140%">
				<feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000000" flood-opacity="0.9" />
			</filter>
		</defs>

		{#each computedRoute.auxiliaryTrails as trail}
			{@render DottedTrail(trail)}
		{/each}

		{#each computedRoute.waypoints as pt}
			{@render StemRoutePoint(pt)}
		{/each}

		{#if computedRoute.routeSvgPath}
			{@render MainRouteRibbon()}
		{/if}

		{#each computedRoute.waypoints as pt}
			{@render GlowingPip(pt)}
		{/each}
	</svg>

	{#snippet DottedTrail(trail: string)}
		<path d={trail} fill="none" stroke="#000000" stroke-width="3" stroke-dasharray="5,6" stroke-linecap="round" opacity="0.6" />
		<path d={trail} fill="none" stroke="#ffffff" stroke-width="2" stroke-dasharray="4,6" stroke-linecap="round" opacity="0.85" />
	{/snippet}

	{#snippet StemRoutePoint(waypoint: Waypoint)}
		<line
			x1={waypoint.lineX}
			y1={waypoint.lineY}
			x2={waypoint.bubbleX}
			y2={waypoint.bubbleY}
			stroke="rgba(255, 255, 255, 0.75)"
			stroke-width="1.8"
			stroke-dasharray="3,3"
			filter="url(#trailShadow)"
		/>
	{/snippet}

	{#snippet MainRouteRibbon()}
		<path
			d={computedRoute.routeSvgPath}
			fill="none"
			stroke="#000000"
			stroke-width="5"
			stroke-linecap="round"
			stroke-linejoin="round"
			opacity="0.5"
		/>
		<path
			d={computedRoute.routeSvgPath}
			fill="none"
			stroke="#ffffff"
			stroke-width="3.2"
			stroke-linecap="round"
			stroke-linejoin="round"
			filter="url(#trailShadow)"
		/>
	{/snippet}

	{#snippet GlowingPip(waypoint: Waypoint)}
		<circle cx={waypoint.lineX} cy={waypoint.lineY} r="6" fill="#ffffff" filter="url(#trailShadow)" />
		<circle cx={waypoint.lineX} cy={waypoint.lineY} r="2.5" fill="#0f172a" />
	{/snippet}
{/snippet}

{#snippet Waypoints()}
	<div class="pointer-events-none absolute inset-0 z-15">
		{#each computedRoute.waypoints as pt}
			{const leftPercent = (pt.bubbleX / SVG_WIDTH) * 100}
			{const topPercent = (pt.bubbleY / SVG_HEIGHT) * 100}

			<div
				class="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
				style="left: {leftPercent}%; top: {topPercent}%;"
			>
				{#if pt.isAbove}
					{@render Label(pt)}
				{/if}

				{@render CircularImage(pt)}

				{#if !pt.isAbove}
					{@render Label(pt)}
				{/if}
			</div>
		{/each}
	</div>

	{#snippet CircularImage(waypoint: Waypoint)}
		<div class="relative flex items-center justify-center">
			<div class="size-10 overflow-hidden rounded-full border-2 border-white bg-neutral-950 shadow-xl sm:size-16">
				{#if waypoint.imageUrl}
					<img class="h-full w-full object-cover object-center" src={waypoint.imageUrl} alt={waypoint.name} />
				{:else}
					<div
						class="flex size-full items-center justify-center rounded-full bg-linear-to-b from-neutral-800 via-neutral-900 to-black text-white"
					>
						<IconCompass class="text-lg text-white/85 sm:text-2xl" />
					</div>
				{/if}
			</div>

			{@render IndexBadge()}
		</div>

		{#snippet IndexBadge()}
			<div
				class="absolute -right-0.5 -bottom-0.5 z-20 flex size-4 items-center justify-center rounded-full border border-white bg-black font-mono text-xs font-bold text-white shadow-md sm:-right-1 sm:-bottom-1 sm:size-5.5 sm:border-2"
			>
				{waypoint.index}
			</div>
		{/snippet}
	{/snippet}

	{#snippet Label(waypoint: Waypoint)}
		<span
			class={{
				'line-clamp-2 max-w-21 text-center text-xs leading-tight font-bold text-white drop-shadow-[0_1px_3px_rgba(0,0,0,1)] sm:line-clamp-none sm:max-w-35': true,
				'mb-1.5 sm:mb-2': waypoint.isAbove,
				'mt-1.5 sm:mt-2': !waypoint.isAbove
			}}
		>
			{waypoint.name}
		</span>
	{/snippet}
{/snippet}

{#snippet ItineraryDetails()}
	<div
		class="pointer-events-none absolute right-4 bottom-3 left-4 z-20 flex items-end justify-between gap-3 sm:right-7 sm:bottom-6 sm:left-7 sm:gap-4"
	>
		<div class="max-w-[75%] sm:max-w-lg">
			{@render Stats()}
		</div>

		<div class="flex shrink-0 items-center gap-1.5 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] sm:gap-2.5">
			{@render BrandWatermark()}
		</div>
	</div>

	{#snippet Stats()}
		{#if stats.length > 0}
			<div class="mb-1.5 flex flex-row flex-nowrap items-center gap-1.5 sm:mb-2.5 sm:gap-2">
				{#each stats as stat (stat.label)}
					<div class="badge badge-soft font-mono badge-xs shadow-lg badge-secondary md:badge-sm">
						<span class="mr-1 opacity-90">{stat.label}:</span>
						<span class="font-bold">{stat.value}</span>
					</div>
				{/each}
			</div>
		{/if}

		<h3 class="line-clamp-2 font-serif text-sm font-bold text-white sm:text-4xl">
			{title}
		</h3>
		<p class="mt-0.5 line-clamp-1 text-xs font-medium text-white/85 sm:text-xs">
			{locationSubtitle}
		</p>
	{/snippet}

	{#snippet BrandWatermark()}
		<span class="hidden size-6 items-center justify-center rounded-full bg-white text-black shadow-md sm:size-8 md:flex">
			<IconCompass class="text-sm sm:text-lg" />
		</span>
		<div class="flex flex-col">
			<span class="font-mono text-xs font-black tracking-widest text-white sm:text-sm">
				{SITE_NAME.toUpperCase()}
			</span>
			<span class="hidden font-sans text-xs font-medium tracking-wider text-white/80 sm:block"> CURATED ROUTE MAP </span>
		</div>
	{/snippet}
{/snippet}

{#snippet ModalFooter()}
	<div class="modal-action mt-0 flex items-center justify-between border-t border-white/10 bg-neutral-900/90 px-6 py-4">
		<span class="text-xs text-neutral-400">
			Curated expedition route overview for {cityName}.
		</span>

		<form method="dialog">
			<button class="btn rounded-full btn-ghost text-white/70 btn-sm hover:text-white"> Close </button>
		</form>
	</div>
{/snippet}

{#snippet ModalBackdrop()}
	<form method="dialog" class="modal-backdrop">
		<button>close</button>
	</form>
{/snippet}
